import { createRenderResourceCache } from './render_resource_cache.mjs';
import { validateNeutralProjection } from './render_projection_contract.mjs';
import { prepareRenderProjection } from './render_geometry_preparation.mjs';

export const RENDER_ASSET_POLICY = 'projection-v1-linear-conic-015-v1';
const check = (value, message) => { if (!value) throw new TypeError(`Render asset: ${message}`); };
const freeze = value => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    for (const child of Object.values(value)) freeze(child);
    Object.freeze(value);
  }
  return value;
};
const id = value => typeof value === 'string' && value.length > 0 && value.length <= 256;

/** G03 asset-projection identity; camera position, object ID and owner are not content identities. */
export function renderProjectionIdentity(reference) {
  const asset = reference?.asset, artifact = reference?.projection;
  check(asset && ['assetId', 'revisionId', 'variantId'].every(key => id(asset[key]))
    && id(reference.occurrenceId) && id(reference.viewId), 'complete asset/variant/occurrence/view identity required');
  check(artifact && typeof artifact.path === 'string' && artifact.path.length <= 1024
    && /^[A-Za-z0-9_./-]+$/.test(artifact.path) && !artifact.path.startsWith('/')
    && artifact.path.split('/').every(p => p && p !== '.' && p !== '..'), 'confined artifact path required');
  check(/^[a-f0-9]{64}$/.test(artifact.sha256) && artifact.mediaType === 'application/json'
    && Number.isSafeInteger(artifact.byteLength) && artifact.byteLength > 0 && artifact.byteLength <= 8 * 1024 * 1024, 'bounded hash-bound JSON required');
  return freeze({ asset: { assetId: asset.assetId, revisionId: asset.revisionId, variantId: asset.variantId },
    occurrenceId: reference.occurrenceId, viewId: reference.viewId,
    projection: { path: artifact.path, sha256: artifact.sha256, byteLength: artifact.byteLength, mediaType: artifact.mediaType } });
}

export function renderProjectionKey(reference) { return JSON.stringify([RENDER_ASSET_POLICY, renderProjectionIdentity(reference)]); }

/** Stream the exact declared length; an oversized response is cancelled before buffering its remainder. */
export async function readRenderArtifact(response, expectedBytes, signal) {
  check(Number.isSafeInteger(expectedBytes) && expectedBytes > 0 && expectedBytes <= 8 * 1024 * 1024, 'artifact size');
  check(response?.ok && response.body?.getReader, 'streaming artifact response required');
  const reader = response.body.getReader();
  const output = new Uint8Array(expectedBytes); let length = 0;
  const abort = () => { Promise.resolve(reader.cancel()).catch(() => undefined); };
  signal?.addEventListener('abort', abort, { once: true });
  try {
    while (true) {
      if (signal?.aborted) throw new Error('Render asset aborted');
      const { done, value } = await reader.read();
      if (signal?.aborted) throw new Error('Render asset aborted');
      if (done) break;
      check(value instanceof Uint8Array && length + value.byteLength <= expectedBytes, 'artifact exceeds declared length');
      output.set(value, length); length += value.byteLength;
    }
    check(length === expectedBytes, 'artifact length mismatch'); return output;
  } catch (error) { try { await reader.cancel(); } catch { /* Preserve original error. */ } throw error; }
  finally { signal?.removeEventListener('abort', abort); reader.releaseLock(); }
}

export function createRenderAssetService({ fetchArtifact, digest, maxBytes = 64 * 1024 * 1024, allowHiddenEdges = false, allowComplexRegions = false } = {}) {
  check(typeof fetchArtifact === 'function' && typeof digest === 'function', 'explicit fetch/hash ports required');
  const sources = createRenderResourceCache({ maxBytes, maxEntries: 64 });
  const prepared = createRenderResourceCache({ maxBytes, maxEntries: 128 });
  let sourceBuilds = 0, geometryBuilds = 0;
  const source = (ref, signal) => sources.acquire(renderProjectionKey(ref), {
    bytes: Math.min(maxBytes, Math.max(65536, ref.projection.byteLength * 20)), signal,
    async create({ signal: requestSignal }) {
      const bytes = await fetchArtifact(ref.projection.path, { signal: requestSignal, byteLength: ref.projection.byteLength });
      check(bytes instanceof Uint8Array && bytes.byteLength === ref.projection.byteLength, 'artifact length mismatch');
      check(await digest(bytes) === ref.projection.sha256, 'artifact hash mismatch');
      if (requestSignal.aborted) throw new Error('Render asset aborted');
      const data = validateNeutralProjection(JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes)), { allowHiddenEdges, allowComplexRegions });
      check(['assetId', 'revisionId', 'variantId'].every(k => data.identity[k] === ref.asset[k])
        && data.occurrenceId === ref.occurrenceId && data.view.viewId === ref.viewId, 'artifact identity mismatch');
      sourceBuilds++;
      return { value: freeze(data), bytes: JSON.stringify(data).length * 2, dispose() { /* Immutable CPU data; cache drops ownership. */ } };
    }
  });
  return {
    acquire(reference, { pixelsPerModelUnit = 1, signal } = {}) {
      const ref = renderProjectionIdentity(reference);
      check(Number.isFinite(pixelsPerModelUnit) && pixelsPerModelUnit >= 1 / 4096 && pixelsPerModelUnit <= 256, 'supported projection scale');
      // Round upward: reused analytical chords retain their 0.15 CSS-pixel bound throughout the bucket.
      const scaleCeiling = 2 ** Math.ceil(Math.log2(pixelsPerModelUnit));
      const key = JSON.stringify([renderProjectionKey(ref), scaleCeiling]);
      return prepared.acquire(key, { bytes: Math.min(maxBytes, 32 * 1024 * 1024), signal,
        async create({ signal: requestSignal }) {
          const lease = await source(ref, requestSignal);
          try {
            const projection = lease.value;
            const geometry = prepareRenderProjection(projection, { assetScale: 1, zoom: scaleCeiling, maxScreenDeviationCssPx: 0.15 });
            const value = freeze({ key, identity: ref, scaleCeiling, bounds: projection.bounds, view: projection.view,
              curves: geometry.curves, regions: projection.regions, report: geometry.report,
              hiddenEdgeMode: projection.hiddenEdgeMode });
            geometryBuilds++;
            return { value, bytes: JSON.stringify(value).length * 2, dispose() { /* CPU ownership only. */ } };
          } finally { lease.release(); }
        } });
    },
    clear() { prepared.clear(); sources.clear(); },
    dispose() { prepared.dispose(); sources.dispose(); },
    snapshot: () => ({ sourceBuilds, geometryBuilds, sources: sources.snapshot(), prepared: prepared.snapshot(),
      memoryScope: 'Conservative reservations and estimated serialized CPU bytes, not native heap/GPU measurements' })
  };
}
